/**
 * Pure scoring helpers for the arcade layer shared across the monograph
 * mini-games. Kept free of React and the DOM so the combo/time-bonus rules are
 * unit-testable and identical across every game.
 *
 * The arcade layer never changes a game's structural verdict (the pure
 * reducers remain the source of truth for what is constructable). It only adds
 * flash-game pressure on top: a combo multiplier that rewards an unbroken run
 * of correct moves, and a time bonus that rewards finishing before the clock
 * runs out.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

/** Each additional combo step adds this much to the multiplier. */
export const COMBO_STEP = 0.5;

/** Default points awarded for a single correct move before the multiplier. */
export const HIT_BASE = 20;

/** Points granted per whole second left on the clock at completion. */
export const TIME_BONUS_PER_SECOND = 5;

/**
 * The score multiplier at a given combo length. A combo of 0 or 1 is 1×; each
 * further unbroken correct move adds {@link COMBO_STEP}.
 */
export function comboMultiplier(combo: number): number {
  const steps = Math.max(0, Math.floor(combo) - 1);
  return 1 + steps * COMBO_STEP;
}

/** Points for one correct move at the current combo length. */
export function pointsForHit(combo: number, base: number = HIT_BASE): number {
  return Math.round(base * comboMultiplier(combo));
}

/** Time bonus for the seconds remaining when a round is completed. */
export function timeBonus(
  remainingSeconds: number,
  perSecond: number = TIME_BONUS_PER_SECOND,
): number {
  return Math.max(0, Math.round(remainingSeconds * perSecond));
}

/** A compact, human-facing rank for an end-of-round score. */
export type ArcadeRank = "S" | "A" | "B" | "C";

export function rankForScore(score: number): ArcadeRank {
  if (score >= 600) return "S";
  if (score >= 400) return "A";
  if (score >= 200) return "B";
  return "C";
}
